import React, { useState } from 'react';
import { Button } from './ui/button';
import { FileText, Check, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get GPS location
const getGPSLocation = () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        console.log('GPS error:', error.message);
        resolve(null); // Return null if denied or error
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  });
};

export const PreviewSignDocument = ({ form, formData, submissionId, onComplete, onClose }) => {
  const [signing, setSigning] = useState(false);
  const [locationStatus, setLocationStatus] = useState('');

  // Use form's brand identity data (from Design tab) - NOT user's business profile
  const brandData = {
    logo: form?.logo_url || null,
    name: form?.company_name || null,
    email: form?.business_email || null,
    phone: form?.business_phone || null,
    address: form?.business_address || null,
    website: form?.business_website || null
  };
  const hasBrandData = brandData.logo || brandData.name || brandData.email || brandData.phone || brandData.address || brandData.website;

  // Check if location capture is enabled for this form
  const locationEnabled = form?.enable_location_capture || false;

  // Find the signature data from the form submission
  const signatureField = (form?.fields || []).find(f => f.type === 'signature');
  const signatureData = signatureField ? formData[signatureField.id] : null;

  const handleSignDocument = async () => {
    setSigning(true);
    try {
      // Get timezone and local time
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const localTime = new Date().toLocaleString('en-US', {
        timeZone: tz,
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
      });

      // Try to extract signer name from form data (look for name fields)
      let signerName = '';
      const nameField = (form?.fields || []).find(f => f.type === 'name' || f.label?.toLowerCase().includes('name'));
      if (nameField && formData[nameField.id]) {
        const nameVal = formData[nameField.id];
        if (typeof nameVal === 'object' && nameVal.first) {
          signerName = `${nameVal.first}${nameVal.last ? ' ' + nameVal.last : ''}`.trim();
        } else if (typeof nameVal === 'string') {
          signerName = nameVal;
        }
      }
      
      // Also try to find email for signer
      let signerEmail = '';
      const emailField = (form?.fields || []).find(f => f.type === 'email' || f.label?.toLowerCase().includes('email'));
      if (emailField && formData[emailField.id]) {
        signerEmail = formData[emailField.id];
      }

      // Get GPS location if enabled
      let locationData = null;
      if (locationEnabled) {
        setLocationStatus('Getting location...');
        locationData = await getGPSLocation();
        if (locationData) {
          setLocationStatus('Location captured');
        } else {
          setLocationStatus('Location unavailable');
        }
      }

      const response = await fetch(`${API_URL}/api/sign/${submissionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature_data: signatureData || '',
          signer_name: signerName,
          signer_email: signerEmail,
          timezone: tz,
          local_time: localTime,
          location: locationData
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to sign document');
      }

      const result = await response.json();
      toast.success('Document signed successfully!');
      onComplete(result);
    } catch (error) {
      toast.error(error.message || 'Failed to sign document');
    } finally {
      setSigning(false);
      setLocationStatus('');
    }
  };

  const accentColor = form?.theme?.accent_color || '#4F46E5';
  const fontFamily = form?.theme?.font_family || 'DM Sans, sans-serif';
  const fields = (form?.fields || []).filter(f =>
    !['heading', 'divider', 'html', 'page_break', 'static_text'].includes(f.type)
  );

  const formatValue = (field, value) => {
    if (!value) return '(empty)';
    if (field.type === 'signature' || (typeof value === 'string' && value.startsWith('data:image'))) {
      return '__SIGNATURE__';
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.values(value).filter(Boolean).join(', ');
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4" data-testid="preview-sign-modal" onClick={(e) => e.stopPropagation()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" style={{ fontFamily }}>
        {/* Header - No close button, signing is mandatory */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            <h2 className="text-base sm:text-lg font-semibold text-slate-900" data-testid="preview-sign-title">Sign Your Document</h2>
          </div>
          <span className="text-xs text-amber-600 font-medium bg-amber-50 px-3 py-1 rounded-full" data-testid="signing-required-badge">Signing Required</span>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {/* Document Preview */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6" data-testid="document-preview">
            {/* Business Letterhead - Uses form's Brand Identity from Design tab */}
            {hasBrandData && (
              <div className="mb-4 pb-3 border-b border-slate-300" data-testid="business-header">
                <div className="flex items-center justify-between">
                  {/* Left: Logo + Company Name */}
                  <div className="flex items-center gap-3">
                    {brandData.logo && (
                      <img
                        src={brandData.logo.startsWith('/api') ? `${API_URL}${brandData.logo}` : brandData.logo}
                        alt=""
                        className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                      />
                    )}
                    {brandData.name && (
                      <p className="font-bold text-slate-900 text-sm sm:text-lg tracking-tight">{brandData.name}</p>
                    )}
                  </div>
                  {/* Right: Contact details with left border */}
                  {(brandData.phone || brandData.email || brandData.address || brandData.website) && (
                    <div className="pl-4 border-l-2 border-slate-300 text-right hidden sm:block">
                      {brandData.phone && (
                        <p className="text-xs text-slate-600">{brandData.phone}</p>
                      )}
                      {brandData.email && (
                        <p className="text-xs text-slate-600">{brandData.email}</p>
                      )}
                      {brandData.website && (
                        <p className="text-xs text-indigo-600">{brandData.website}</p>
                      )}
                      {brandData.address && (
                        <p className="text-xs text-slate-500">{brandData.address}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="text-right text-xs sm:text-sm text-slate-500 mb-3 sm:mb-4">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 sm:mb-6 pb-3 border-b-2" style={{ borderColor: accentColor }}>
              {form?.title || 'Form Submission'}
            </h3>

            <div className="space-y-2 sm:space-y-3">
              {fields.map((field) => {
                const rawValue = formData[field.id];
                const displayValue = formatValue(field, rawValue);

                return (
                  <div key={field.id} className="flex flex-col sm:flex-row sm:gap-4 py-2 border-b border-slate-100 last:border-0" data-testid={`preview-field-${field.id}`}>
                    <span className="font-semibold text-slate-800 text-xs sm:text-sm sm:min-w-[140px] shrink-0">
                      {field.label}
                    </span>
                    <span className="text-sm text-slate-600 break-all">
                      {displayValue === '__SIGNATURE__' ? (
                        <img src={rawValue} alt="Signature" className="h-10 sm:h-12 object-contain" />
                      ) : (
                        displayValue
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Consent */}
          <div className="text-center mb-2 px-2" data-testid="consent-text">
            <p className="text-xs text-slate-500 leading-relaxed">
              By signing, I agree that my e-signature is as valid as a handwritten signature.{' '}
              <a
                href="/consumer-consent"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-700 underline"
                data-testid="consent-disclosure-link"
              >
                Disclosure
              </a>
            </p>
          </div>
        </div>

        {/* Footer with Sign Button */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <Button
            onClick={handleSignDocument}
            disabled={signing}
            className="w-full h-12 text-white font-semibold text-base rounded-xl bg-blue-600 hover:bg-blue-700"
            data-testid="sign-document-btn"
          >
            {signing ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Signing...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                Sign Document
              </span>
            )}
          </Button>
          <p className="text-center text-[10px] text-slate-400 mt-2">Powered by Formlio Sign</p>
        </div>
      </div>
    </div>
  );
};
